<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import Avatar from '../Avatar/Avatar.vue'
import { cn } from '@/lib/utils'
import netwatchLogo from '@/assets/netwatch.svg'
import aiIcon from '@/assets/ai-icon.svg'
import { getSvgCSS } from '@/utils'
import { useChatStore } from '@/stores/useChat'

const props = defineProps<{
  visible: boolean
  close: () => void
  expand: () => void
  isExpanded: boolean
  activeTab: string
  onTabChange: (tab: string) => void
  apiHost?: string
  clientId?: string
}>()

const isSmallScreen = useMediaQuery('(min-width: 639.9px)')
const chatStore = useChatStore()

const netwatchLogoCss = ref<string>()
const aiIconCss = ref<string>()

onMounted(async () => {
  netwatchLogoCss.value = await getSvgCSS(netwatchLogo)
  aiIconCss.value = await getSvgCSS(aiIcon)
})
</script>

<template>
  <div
    :class="cn(
      'header-container',
      $attrs.class ?? '',
    )"
  >
    <div
      :class="cn(
        'header-wrapper',
      )"
    >
      <div>
        <div
          :class="cn(
            'header',
            props.visible === true ? 'fade-enter-done' : '',
          )"
        >
          <div>
            <nav class="header-nav">
              <div class="header-nav-content">
                <div
                  v-if="props.activeTab === 'faq' || props.activeTab === 'reports'"
                  class="header-action-button-container"
                >
                  <button
                    type="button"
                    aria-label="go back to chat"
                    class="header-action-button"
                    tabindex="0"
                    @click="props.onTabChange('chat')"
                  >
                    <div
                      class="i-ph-caret-left-bold text-xl"
                    />
                  </button>
                </div>
                <button class="header-button" tabindex="0">
                  <Avatar />
                  <h1 class="header-title-netwatch-bot">
                    Netwatch
                  </h1>
                  <div class="header-subtitle">
                    <div :style="aiIconCss" />
                    Bot
                  </div>
                </button>
                <div class="header-end">
                  <div
                    aria-label="Reports"
                    role="button"
                    class="header-action-button"
                    :class="cn(props.activeTab === 'reports' ? 'header-action-button-active' : '')"
                    @click="props.onTabChange('reports')"
                  >
                    <div class="i-material-symbols-bar-chart text-xl" /> Reports
                  </div>
                  <div
                    aria-label="FAQ"
                    role="button"
                    class="header-action-button"
                    :class="cn(props.activeTab === 'faq' ? 'header-action-button-active' : '')"
                    @click="props.onTabChange('faq')"
                  >
                    <div class="i-material-symbols-help text-xl" /> FAQ
                  </div>
                  <div
                    v-if="isSmallScreen"
                    aria-label="New Chat"
                    role="button"
                    class="header-action-button"
                    @click="() => chatStore.clearChat(props.apiHost, props.clientId)"
                  >
                    <div class="i-material-symbols-delete-forever text-xl" />
                  </div>
                  <div
                    v-if="isSmallScreen"
                    aria-label="Expand"
                    :aria-hidden="isSmallScreen"
                    role="button"
                    class="header-action-button"
                    @click="props.expand"
                  >
                    <div :class="cn('text-xl', isExpanded ? 'i-ci-shrink' : 'i-ci-expand')" />
                  </div>
                  <div
                    v-if="!isSmallScreen"
                    aria-label="Close"
                    :aria-hidden="!isSmallScreen"
                    role="button"
                    class="header-action-button"
                    @click="props.close"
                  >
                    <div class="i-ph-x-bold" />
                  </div>
                </div>
              </div>
            </nav>

            <!-- Tab Navigation -->
            <div class="tab-navigation">
              <div class="tab-container">
                <button
                  :class="cn(
                    'tab-button',
                    props.activeTab === 'chat' ? 'tab-active' : 'tab-inactive',
                  )"
                  @click="props.onTabChange('chat')"
                >
                  <div class="i-material-symbols-chat text-lg" />
                  <span class="tab-label">Chat</span>
                </button>
                <button
                  :class="cn(
                    'tab-button',
                    props.activeTab === 'faq' ? 'tab-active' : 'tab-inactive',
                  )"
                  @click="props.onTabChange('faq')"
                >
                  <div class="i-material-symbols-help text-lg" />
                  <span class="tab-label">FAQ</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tab-navigation {
  background: rgba(255, 255, 255, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0 8px;
}

.tab-container {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  background: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  min-width: 80px;
  justify-content: center;
}

.tab-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

.tab-active {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.tab-inactive {
  color: rgba(255, 255, 255, 0.7);
}

.tab-label {
  font-size: 12px;
  font-weight: 500;
}

.header-action-button-active {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}
</style>
